import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, LayoutDashboard, Search, Menu, X, Lightbulb } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { NotificationBell } from "@/components/NotificationBell";
import api from "@/lib/api";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAuth = user && user !== false;
  const [menuOpen, setMenuOpen] = useState(false);
  const [pendingLeads, setPendingLeads] = useState(0);

  useEffect(() => {
    if (isAuth && ["collector", "customer", "company"].includes(user.role)) {
      api.get("/leads/pending-count").then(({ data }) => setPendingLeads(data.count || 0)).catch(() => {});
    } else {
      setPendingLeads(0);
    }
  }, [isAuth, user]);

  const badgeTitle = user?.role === "collector" || user?.role === "company"
    ? `${pendingLeads} open leads waiting`
    : `${pendingLeads} reviews pending`;

  const baseDash = isAuth
    ? user.role === "admin" ? "/admin" : user.role === "collector" ? "/dashboard/collector" : user.role === "company" ? "/dashboard/company" : "/dashboard/customer"
    : "/";
  const dashHref = pendingLeads > 0 && ["collector", "company"].includes(user?.role) ? `${baseDash}?tab=leads` : baseDash;

  const doLogout = async () => { setMenuOpen(false); await logout(); navigate("/"); };
  const linkBase = "text-sm font-medium text-[#1E3F33] hover:text-[#CC5A3A]";

  return (
    <header className="bg-white/80 backdrop-blur-xl border-b border-[#1E3F33]/5 sticky top-0 z-50">
      <div className="container-x flex items-center justify-between h-16">
        <Link
          to="/"
          data-testid="nav-home-link"
          className="flex items-center gap-2 font-heading text-base sm:text-lg font-semibold text-[#1E3F33] leading-tight"
        >
          <img
            src={`${process.env.PUBLIC_URL}/ssm-logo.webp`}
            alt="Screening Services Mobile logo"
            data-testid="site-logo"
            className="w-10 h-10 rounded-full object-contain shrink-0 ring-1 ring-[#1E3F33]/10 bg-white"
          />
          <span className="hidden sm:inline text-[#B91C1C]">Screening Services Mobile</span>
          <span className="sm:hidden text-[#B91C1C]">Screening Services</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden sm:flex items-center gap-4">
          <Link to="/search" data-testid="nav-search-link" className={`${linkBase} px-3 py-2 inline-flex items-center gap-1.5`}>
            <Search className="w-4 h-4" strokeWidth={1.6} /> Find Collectors
          </Link>
          <Link to="/suggestions" data-testid="nav-suggestions-link" className={`${linkBase} px-3 py-2 inline-flex items-center gap-1.5`}>
            <Lightbulb className="w-4 h-4" strokeWidth={1.6} /> Suggestions
          </Link>
          {!isAuth && user !== null && (
            <>
              <Link to="/login" data-testid="nav-login-link" className={`${linkBase} px-3 py-2`}>Sign in</Link>
              <Link to="/register" data-testid="nav-register-link" className="btn-primary text-sm py-2 px-5">Get started</Link>
            </>
          )}
          {isAuth && (
            <>
              <NotificationBell />
              <Link to={dashHref} data-testid="nav-dashboard-link" className={`${linkBase} px-3 py-2 inline-flex items-center gap-1.5 relative`}>
                <LayoutDashboard className="w-4 h-4" strokeWidth={1.6} /> Dashboard
                {pendingLeads > 0 && (
                  <span data-testid="nav-pending-leads-badge" title={badgeTitle} className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-[#CC5A3A] rounded-full">
                    {pendingLeads > 99 ? "99+" : pendingLeads}
                  </span>
                )}
              </Link>
              <button data-testid="nav-logout-button" onClick={doLogout} className={`${linkBase} px-3 py-2 inline-flex items-center gap-1.5`}>
                <LogOut className="w-4 h-4" strokeWidth={1.6} /> Logout
              </button>
            </>
          )}
        </nav>

        {/* Mobile controls */}
        <div className="flex items-center gap-1.5 sm:hidden">
          {isAuth && <NotificationBell />}
          <button
            data-testid="nav-menu-toggle"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
            className="p-2 rounded-lg text-[#1E3F33] hover:bg-[#1E3F33]/5"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown menu */}
      {menuOpen && (
        <nav data-testid="mobile-menu" className="sm:hidden border-t border-[#1E3F33]/8 bg-white/95 backdrop-blur-xl">
          <div className="container-x py-3 flex flex-col">
            <Link to="/search" data-testid="mobile-nav-search-link" onClick={() => setMenuOpen(false)} className={`${linkBase} py-3 inline-flex items-center gap-2`}>
              <Search className="w-4 h-4" strokeWidth={1.6} /> Find Collectors
            </Link>
            <Link to="/suggestions" data-testid="mobile-nav-suggestions-link" onClick={() => setMenuOpen(false)} className={`${linkBase} py-3 inline-flex items-center gap-2`}>
              <Lightbulb className="w-4 h-4" strokeWidth={1.6} /> Suggestions
            </Link>
            {!isAuth && user !== null && (
              <>
                <Link to="/login" data-testid="mobile-nav-login-link" onClick={() => setMenuOpen(false)} className={`${linkBase} py-3`}>Sign in</Link>
                <Link to="/register" data-testid="mobile-nav-register-link" onClick={() => setMenuOpen(false)} className="btn-primary text-sm py-2.5 px-5 mt-2 text-center">Get started</Link>
              </>
            )}
            {isAuth && (
              <>
                <Link to={dashHref} data-testid="mobile-nav-dashboard-link" onClick={() => setMenuOpen(false)} className={`${linkBase} py-3 inline-flex items-center gap-2`}>
                  <LayoutDashboard className="w-4 h-4" strokeWidth={1.6} /> Dashboard
                  {pendingLeads > 0 && (
                    <span data-testid="mobile-nav-pending-leads-badge" className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-[#CC5A3A] rounded-full">
                      {pendingLeads > 99 ? "99+" : pendingLeads}
                    </span>
                  )}
                </Link>
                <button data-testid="mobile-nav-logout-button" onClick={doLogout} className={`${linkBase} py-3 inline-flex items-center gap-2 text-left`}>
                  <LogOut className="w-4 h-4" strokeWidth={1.6} /> Logout
                </button>
              </>
            )}
          </div>
        </nav>
      )}
    </header>
  );
}
